import React from "react";
import styled from "styled-components";
import PortableText from "@sanity/block-content-to-react";
import { navigate } from "gatsby";

import {
  fontSizes,
  isAnathoth,
  LAPTOP_BREAKPOINT,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
  TWENTY_INCH_SCREEN,
} from "@util/constants";
import { Container } from "@util/standard";
import { urlForImageRef } from "@lib/sanityClient";
import { Button } from "@global";

const SITE_IBAN = process.env.GATSBY_IBAN || "nz";

interface Props {
  blocks: any;
  fontSize?: number;
  textButton?: boolean;
}

const Img = styled(Container) <{ url?: string }>`
  margin: 20px 0px 35px 0px;
  height: 700px;
  width: 100%;
  ${({ url }) => url && `background-image: url(${url})`};
  background-position: center;
  background-repeat: no-repeat;

  @media only screen and (max-width: ${TWENTY_INCH_SCREEN}px) {
    height: 600px;
  }

  @media only screen and (max-width: ${LAPTOP_BREAKPOINT}px) {
    height: 500px;
  }

  @media only screen and (max-width: ${TABLET_BREAKPOINT}px) {
    height: 400px;
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 300px;
    margin: 20px 0px;
  }

  span {
    color: white;
  }
`;

const heighlight = (props: any) => {
  return <span style={{ color: "red" }}>{props.children}</span>;
};

const line = (props: any) => {
  return (
    <span>
      {props.children}
      <hr style={{ backgroundColor: "white" }} />
    </span>
  );
};

const leftAlign = (props: any) => {
  return (
    <span
      style={{
        textAlign: "left",
        width: "100%",
        display: "block",
      }}
    >
      {props.children}
    </span>
  );
};

const RightAlign = (props: any) => {
  return (
    <span
      style={{
        textAlign: "right",
        width: "100%",
        display: "block",
      }}
    >
      {props.children}
    </span>
  );
};

const CenterAlign = (props: any) => {
  return (
    <span
      style={{
        textAlign: "center",
        width: "100%",
        display: "block",
      }}
    >
      {props.children}
    </span>
  );
};

const productReference = (props: any) => {
  const ref = props.mark?.reference;
  const slug = ref?.content?.main?.slug?.current;
  const auTwinSlug = ref?.auStoreProduct?.content?.main?.slug?.current;

  if (!ref?._type) {
    return <span>{props.children}</span>;
  }

  if (!slug && !(ref._type === "product" && SITE_IBAN === "au" && auTwinSlug)) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("blocksContent: productReference missing slug", ref);
    }
    return <span>{props.children}</span>;
  }

  if (SITE_IBAN === "au") {
    if (ref._type === "productAu") {
      return <a href={`/shop/${slug}`}>{props.children}</a>;
    }
    if (ref._type === "product" && auTwinSlug) {
      return <a href={`/shop/${auTwinSlug}`}>{props.children}</a>;
    }
    if (ref._type === "product") {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "blocksContent: AU site — NZ product in rich text needs auStoreProduct on the NZ product (or use an AU product in the link).",
          ref._id,
        );
      }
      return <span>{props.children}</span>;
    }
    return <span>{props.children}</span>;
  }

  if (ref._type === "product") {
    return <a href={`/shop/${slug}`}>{props.children}</a>;
  }

  return (
    <a
      href={`https://www.barkers.com.au/shop/${slug}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children}
    </a>
  );
};

const Link = (props: any) => {
  if (!props) return null;

  const buttonTheme = isAnathoth ? "base" : "transparent";

  const setAlignment = () => {
    if (props.node.alignment === "left") return "flex-start";
    if (props.node.alignment === "right") return "flex-end";
    if (props.node.alignment === "center") return "center";
    return "flex-start";
  };

  if (!props.node.internallink) {
    return (
      <Container className="link-wrapper" justifyContent={setAlignment()}>
        <Button
          linkTo={props.node.internallink ? props.node.url : undefined}
          text={props.node.linktext}
          theme={buttonTheme}
          linkToNewWindow={props.node.newwindow ? props.node.url : undefined}
        />
      </Container>
    );
  }

  return (
    <Container className="link-wrapper" justifyContent={setAlignment()}>
      <Button
        linkTo={props.node.url}
        text={props.node.linktext}
        theme={buttonTheme}
      />
    </Container>
  );
};

const fontFamily = (props: any) => {

  return (
    <span
      style={{
        fontFamily: props?.mark?.fontFamily,
        fontWeight: props?.mark?.fontWeight,
      }}
    >
      {props.children}
    </span>
  );
};

const serializer = {
  types: {
    link: Link,
    image: (props: any) => {
      return <Img url={urlForImageRef(props.node.asset)?.url() as string} />;
    },
  },
  marks: {
    heighlight,
    line,
    leftAlign,
    RightAlign,
    CenterAlign,
    productReference,
    fontFamily,
  },
};

const serializerText = {
  types: {
    link: (props: any) => {
      return (
        <a
          onClick={() => {
            navigate(`/${props.node.url}`);
          }}
        >
          {props.node.linktext}
        </a>
      );
    },
    image: (props: any) => {
      return <Img url={urlForImageRef(props.node.asset)?.url() as string} />;
    },
  },
  marks: {
    heighlight,
    line,
    leftAlign,
    RightAlign,
    CenterAlign,
    productReference,
  },
};

const StyledPortableText = styled(Container) <{ fontSize?: number }>`
  width: 100%;
  h1 {
    font-size: ${fontSizes.h1.default}px;
    @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      font-size: ${fontSizes.h1.mobile}px;
      line-height: ${fontSizes.h1.mobile * 1.2}px;
    }
  }

  h2 {
    font-size: ${fontSizes.h2.default}px;
    @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      font-size: ${fontSizes.h2.mobile}px;
      line-height: ${fontSizes.h2.mobile * 1.2}px;
    }
  }

  h3 {
    font-size: ${fontSizes.h3.default}px;
    @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      font-size: ${fontSizes.h3.mobile}px;
      line-height: ${fontSizes.h3.mobile * 1.2}px;
    }
  }

  h6 {
    font-size: 18px;
    font-weight: 500;
  }

  p {
    min-height: 16px;
    font-size: ${props =>
    props.fontSize ? props.fontSize : fontSizes.p.default}px;
    margin-bottom: 24px;
    @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      font-size: ${fontSizes.p.mobile}px;
      line-height: 1.2;
    }
  }

  a {
    text-decoration: underline;
    color: inherit;
  }
  .blocksContent--wrapper {
    width: 100%;
  }
`;

const BlocksContent = ({ blocks, fontSize, textButton }: Props) => {
  if (!blocks) return null;

  const invalidBlock = () => {
    let hasInvalidBlock = false;
    blocks.forEach((block: any) => {
      if (!block || !block?.children) return;
      block.children.forEach((child: any) => {
        if (!child) return;
        if (!child.text && !child.marks) hasInvalidBlock = true;
      });
    });
    return hasInvalidBlock;
  };

  // Make sure the entire page will not fail to load when there's an error with the rawblocks json
  if (invalidBlock() === true) return null;

  return (
    <StyledPortableText fontSize={fontSize} className="block-content">
      <PortableText
        blocks={blocks}
        className="blocksContent--wrapper"
        serializers={textButton ? serializerText : serializer}
        imageOptions={{ auto: "format", fit: "fill" }}
        projectId={process.env.GATSBY_SANITY_PROJECT_ID}
        dataset={process.env.GATSBY_NODE_ENV ?? "development"}
      />
    </StyledPortableText>
  );
};

export default BlocksContent;
